import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  LaneMarketRateValidationError,
  toLaneMarketRateRequest,
  useLaneSuggestedPrice,
  useSuggestedPrice,
} from '#/features/market-rate'
import type { MarketRateRow } from '#/features/market-rate'
import { toast } from '#/shared/ui/sonner'
import { moneyToFormString } from '#/widgets/payment-card'

import type {
  LocationFormData,
  ShipmentFormData,
  VehiclesFormData,
} from '#/features/order-create'

interface UsePriceCheckParams {
  shipmentId?: string
  getLocation: () => LocationFormData
  getVehicles: () => VehiclesFormData
  getShipment: () => ShipmentFormData
  validateLocation: () => Promise<boolean>
  validateVehicles: () => Promise<boolean>
  applyTariff: (formattedTariff: string) => void
}

export function usePriceCheck({
  shipmentId,
  getLocation,
  getVehicles,
  getShipment,
  validateLocation,
  validateVehicles,
  applyTariff,
}: UsePriceCheckParams) {
  const { t } = useTranslation()
  const lane = useLaneSuggestedPrice()
  const [open, setOpen] = useState(false)
  const useId = Boolean(shipmentId)
  const idMode = useSuggestedPrice({ shipmentId, enabled: useId && open })

  const handlePriceCheck = async () => {
    if (useId) {
      setOpen(true)
      return
    }
    const [locationOk, vehiclesOk] = await Promise.all([
      validateLocation(),
      validateVehicles(),
    ])
    if (!locationOk) {
      toast.error(t('orderCreate.priceCheck.locationRequired'))
      return
    }
    if (!vehiclesOk) {
      toast.error(t('orderCreate.priceCheck.vehiclesRequired'))
      return
    }
    try {
      const request = toLaneMarketRateRequest({
        location: getLocation(),
        vehicles: getVehicles(),
        shipment: getShipment(),
      })
      setOpen(true)
      await lane.fetch(request)
    } catch (error) {
      if (error instanceof LaneMarketRateValidationError) {
        toast.error(t(`orderCreate.priceCheck.${error.reason}Required`))
        return
      }
      throw error
    }
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next && !useId) lane.reset()
  }

  const handleUseRate = (row: MarketRateRow) => {
    const rate = row.actualPrice ?? row.listedPrice
    applyTariff(moneyToFormString(rate))
  }

  const source = useId ? idMode : lane

  return {
    open,
    onOpenChange: handleOpenChange,
    onPriceCheck: handlePriceCheck,
    onUseRate: handleUseRate,
    stats: source.stats,
    rows: source.rows,
    isLoading: source.isLoading,
    errorMessage: source.errorMessage,
  }
}
